import * as React from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Link from "@mui/joy/Link";
import Input from "@mui/joy/Input";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import Alert from "@mui/joy/Alert";
import GoogleIcon from "./GoogleIcon";
import { useNavigate } from "react-router-dom";
import settings from "../../utils/settings.json";
import { auth } from "../../utils/FireStore"; // Import the auth instance
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { createUser } from "../../utils/api/user";
import FenceLinkNameLogo from "../../assets/icons/name-logo/Component 1.svg";

function ColorSchemeToggle() {
  return <></>;
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [emailError, setEmailError] = React.useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return "Password must be at least 8 characters long";
    }
    if (!hasUpperCase) {
      return "Password must contain at least one uppercase letter";
    }
    if (!hasLowerCase) {
      return "Password must contain at least one lowercase letter";
    }
    if (!hasNumbers) {
      return "Password must contain at least one number";
    }
    if (!hasSpecialChar) {
      return "Password must contain at least one special character";
    }
    return "";
  };

  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      setError("");
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Create user document in Firestore
      await createUser({ 
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      });
      
      navigate(`/dashboard`);
    } catch (error) {
      console.error("Error signing up with Google:", error);
      setError("Failed to sign up with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          ":root": {
            "--Form-maxWidth": "800px",
            "--Transition-duration": "0.4s", // set to `none` to disable transition
          },
        }}
      />
      <Box
        sx={(theme) => ({
          width: { xs: "100%", md: "50vw" },
          transition: "width var(--Transition-duration)",
          transitionDelay: "calc(var(--Transition-duration) + 0.1s)",
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "flex-end",
          backdropFilter: "blur(12px)",
          backgroundColor: "rgba(255 255 255 / 0.2)",
          [theme.getColorSchemeSelector("light")]: {
            backgroundColor: "rgba(255, 255, 255 / 0.4)",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100dvh",
            width: "100%",
            px: 2,
          }}
        >
          <Box
            component="header"
            sx={{
              py: 3,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ gap: 2, display: "flex", alignItems: "center" }}>
              <Box sx={{ gap: 2, display: "flex", alignItems: "center" }}>
                <img width={160} src={FenceLinkNameLogo} alt="FenceLink icon" />
              </Box>
            </Box>
            <ColorSchemeToggle />
          </Box>
          <Box
            component="main"
            sx={{
              my: "auto",
              py: 2,
              pb: 5,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              maxWidth: "500px",
              mx: "auto",
              borderRadius: "sm",
              "& form": {
                display: "flex",
                flexDirection: "column",
                gap: 2,
              },
              [`& .MuiFormLabel-asterisk`]: {
                visibility: "hidden",
              },
            }}
          >
            <Stack gap={4} sx={{ mb: 2 }}>
              <Stack gap={1}>
                <Typography component="h1" level="h3">
                  Sign Up to {settings.companyName}
                </Typography>
                <Typography level="body-sm">
                  Already have an account?{" "}
                  <Link onClick={() => navigate(`/sign-in`)} level="title-sm">
                    Sign In
                  </Link>
                </Typography>
              </Stack>
              <Button
                variant="soft"
                color="neutral"
                fullWidth
                startDecorator={<GoogleIcon />}
                onClick={handleGoogleSignUp}
                loading={loading}
                disabled={loading}
              >
                Continue with Google
              </Button>
            </Stack>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}
            <Divider
              sx={(theme) => ({
                [theme.getColorSchemeSelector("light")]: {
                  color: { xs: "#FFF", md: "text.tertiary" },
                },
              })}
            ></Divider>
            <Stack gap={4} sx={{ mt: 2 }}>
              <form
                onSubmit={async (event) => {
                  try {
                    setLoading(true);
                    setError("");
                    setPasswordError("");
                    setEmailError("");
                    event.preventDefault();
                    const formElements = event.currentTarget.elements;
                    const data = {
                      email: formElements.email.value,
                      password: formElements.password.value,
                      confirmPassword: formElements.confirmPassword.value,
                    };

                    // Validate email
                    const emailValidationError = validateEmail(data.email);
                    if (emailValidationError) {
                      setEmailError(emailValidationError);
                      return;
                    }

                    // Validate password
                    const passwordValidationError = validatePassword(data.password);
                    if (passwordValidationError) {
                      setPasswordError(passwordValidationError);
                      return;
                    }

                    // Check if passwords match
                    if (data.password !== data.confirmPassword) {
                      setPasswordError("Passwords do not match");
                      return;
                    }

                    await createUserWithEmailAndPassword(
                      auth,
                      data.email,
                      data.password
                    );

                    // Create user document in Firestore
                    // await createUser({ 
                    //   email: data.email,
                    // });

                    navigate(`/dashboard`);
                  } catch (error) {
                    console.error("Error signing up:", error);
                    setError(error.message || "Failed to create account. Please try again.");
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                <FormControl required>
                  <FormLabel>Email</FormLabel>
                  <Input type="email" name="email" />
                </FormControl>
                {emailError && (
                  <Alert color="danger" variant="soft" size="sm">
                    {emailError}
                  </Alert>
                )}
                <FormControl required>
                  <FormLabel>Password</FormLabel>
                  <Input type="password" name="password" />
                  <Typography level="body-xs" sx={{ mt: 0.5, color: "text.tertiary" }}>
                    Must be at least 8 characters with uppercase, lowercase, number, and special character
                  </Typography>
                </FormControl>
                <FormControl required>
                  <FormLabel>Confirm Password</FormLabel>
                  <Input type="password" name="confirmPassword" />
                </FormControl>
                {passwordError && (
                  <Alert color="danger" variant="soft" size="sm">
                    {passwordError}
                  </Alert>
                )}

                <Stack gap={4} sx={{ mt: 2 }}>
                  <Button type="submit" fullWidth loading={loading} disabled={loading}>
                    Sign Up
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Box>
          <Box component="footer" sx={{ py: 3 }}>
            <Typography level="body-xs" textAlign="center">
              © {settings.companyName} {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={(theme) => ({
          height: "100%",
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          left: { xs: 0, md: "50vw" },
          transition:
            "background-image var(--Transition-duration), left var(--Transition-duration) !important",
          transitionDelay: "calc(var(--Transition-duration) + 0.1s)",
          backgroundColor: "background.level1",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundImage:
            "url(https://images.unsplash.com/photo-1533803786316-b85d29b59a7f?q=80&w=3500&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)",
          [theme.getColorSchemeSelector("light")]: {
            backgroundImage:
              "url(https://images.unsplash.com/photo-1533803786316-b85d29b59a7f?q=80&w=3500&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)",
          },
        })}
      />
    </>
  );
}
